import React, { useCallback, useMemo } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useMeContext } from '~/modules/me';
import { getError } from '~/util';
import useFocusListElementEffect from '~/modules/common/hooks/useFocusListElementEffect';
import TaskResourceEstimatesHeader from './TaskResourceEstimatesHeader';
import TaskResourceEstimatesFooter from './TaskResourceEstimatesFooter';
import TaskResourceEstimateRow from './TaskResourceEstimateRow';

const useStyles = makeStyles(theme => ({
  container: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minWidth: 'max-content'
  },
  title: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightBold,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1)
  },
  emptyMessage: {
    color: theme.palette.text.secondary
  }
}));

const getValueOrDefault = value => value || 0;

export const getTotalEstimatedValues = taskResourceEstimates =>
  taskResourceEstimates.reduce(
    (retVal, { estimatedHours, resource, role }) => ({
      totalEstimatedHours:
        retVal.totalEstimatedHours + getValueOrDefault(estimatedHours),
      totalEstimatedAmount:
        retVal.totalEstimatedAmount +
        getValueOrDefault(
          resource
            ? resource.userRateInProjectCurrency?.amount
            : role?.currentRate?.amount
        ) *
          getValueOrDefault(estimatedHours)
    }),
    { totalEstimatedHours: 0, totalEstimatedAmount: 0 }
  );

const getDynamicDataColumnWidth = ({ dialogWidth, isFullVersionEnabled }) => {
  const resourcingAreaWidth = dialogWidth - 80;
  const removeColumnWidth = 5 * 8;
  const availableSpace = resourcingAreaWidth - removeColumnWidth;
  const dataColumnCount = isFullVersionEnabled ? 4 : 3;

  return Math.floor(Math.max(18 * 12, availableSpace / dataColumnCount));
};

const handleDeleteFocus = ({
  deletedRowId,
  resourceEstimates,
  remainingAssignments,
  setResourceEstimateRowNextFocus
}) => {
  if (!setResourceEstimateRowNextFocus) return;

  const deletedIndex = resourceEstimates.findIndex(
    estimate => estimate.rowId === deletedRowId
  );

  const nextFocusRowId =
    remainingAssignments[deletedIndex]?.rowId ||
    remainingAssignments[deletedIndex - 1]?.rowId ||
    remainingAssignments[0]?.rowId ||
    null;

  setResourceEstimateRowNextFocus(nextFocusRowId);
};

export const TaskResourceEstimates = ({
  taskEstimatedCost,
  taskEstimatedHours,
  onResourceEstimateAdd,
  resourceEstimates,
  setFieldValue,
  projectId,
  projectSlug,
  projectCurrency,
  errors,
  dialogWidth,
  canViewCost,
  values
}) => {
  const classes = useStyles();

  const {
    fallbackElementRef: addResourceAssignmentButtonRef,
    getMatchingRowElementRef: getMatchingResourceEstimateRowElementRef,
    setNextFocus: setResourceEstimateRowNextFocus
  } = useFocusListElementEffect();

  const { permissionsMap } = useMeContext();
  const isFullVersionEnabled = Boolean(
    permissionsMap['urn:replicon-webui:completed-resource-allocation:edit']
  );

  const dynamicDataColumnWidth = getDynamicDataColumnWidth({
    dialogWidth,
    isFullVersionEnabled
  });

  const styleProps = useMemo(
    () => ({
      gridItemStyle: {
        width: `${dynamicDataColumnWidth}px`
      },
      removeColumnStyle: {
        display: 'flex',
        alignItems: `center`,
        width: `40px`,
        paddingLeft: `0px`
      }
    }),
    [dynamicDataColumnWidth]
  );

  const { formatMessage } = useIntl();
  const { totalEstimatedHours, totalEstimatedAmount } = getTotalEstimatedValues(
    resourceEstimates
  );

  const totalEstimatedCost = useMemo(
    () => ({
      amount: totalEstimatedAmount,
      currencySymbol: projectCurrency?.displayText
    }),
    [totalEstimatedAmount, projectCurrency]
  );

  const onResourceEstimateChange = useCallback(
    ({ assignment: modifiedAssignment, isDelete }) => {
      const updatedResourceAssignments = resourceEstimates.reduce(
        (acc, assignment) => {
          if (assignment.rowId === modifiedAssignment.rowId) {
            if (isDelete) {
              return acc;
            }
            acc.push(modifiedAssignment);
          } else {
            acc.push(assignment);
          }

          return acc;
        },
        []
      );

      if (isDelete) {
        handleDeleteFocus({
          deletedRowId: modifiedAssignment.rowId,
          resourceEstimates,
          remainingAssignments: updatedResourceAssignments,
          setResourceEstimateRowNextFocus
        });
      }

      setFieldValue('resourceEstimates', updatedResourceAssignments);
    },
    [resourceEstimates, setFieldValue, setResourceEstimateRowNextFocus]
  );
  const selectedResourceIds = resourceEstimates.map(
    estimate => estimate?.resource?.id
  );

  const taskEstimatedCostWithDefaultAmount = useMemo(
    () => ({
      ...taskEstimatedCost,
      amount: getValueOrDefault(taskEstimatedCost?.amount)
    }),
    [taskEstimatedCost]
  );

  return (
    <div className={classes.container}>
      {resourceEstimates.length === 0 && (
        <>
          <Typography className={classes.title}>
            {formatMessage({ id: 'taskResourceAssignments.resources' })}
          </Typography>
          <span className={classes.emptyMessage}>
            {formatMessage({ id: 'taskResourceAssignments.emptyMessage' })}
          </span>
        </>
      )}
      {resourceEstimates.length > 0 && (
        <TaskResourceEstimatesHeader
          styleProps={styleProps}
          isFullVersionEnabled={isFullVersionEnabled}
        />
      )}
      {(resourceEstimates || []).map((resourceEstimate, index) => (
        <TaskResourceEstimateRow
          key={resourceEstimate.rowId}
          styleProps={styleProps}
          onResourceEstimateChange={onResourceEstimateChange}
          setFieldValue={setFieldValue}
          resourceEstimate={resourceEstimate}
          projectSlug={projectSlug}
          projectId={projectId}
          projectCurrency={projectCurrency}
          selectedResourceIds={selectedResourceIds}
          error={getError(errors, `resourceEstimates[${index}]`)}
          canViewCost={canViewCost}
          values={values}
          isFullVersionEnabled={isFullVersionEnabled}
          getMatchingRowElementRef={getMatchingResourceEstimateRowElementRef}
          autoFocusRoleDropdown={index === resourceEstimates.length - 1}
        />
      ))}
      <TaskResourceEstimatesFooter
        taskEstimatedCost={taskEstimatedCostWithDefaultAmount}
        styleProps={styleProps}
        taskEstimatedHours={getValueOrDefault(taskEstimatedHours)}
        onResourceEstimateAdd={onResourceEstimateAdd}
        showSummary={resourceEstimates.length > 0}
        totalEstimatedHours={totalEstimatedHours}
        totalEstimatedCost={totalEstimatedCost}
        canViewCost={canViewCost}
        projectCurrency={projectCurrency}
        initialAllocateResourceTimeValue={
          values.initialAllocateResourceTimeValue
        }
        addResourceAssignmentButtonRef={addResourceAssignmentButtonRef}
      />
    </div>
  );
};

TaskResourceEstimates.propTypes = {
  errors: PropTypes.object,
  taskEstimatedHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onResourceEstimateAdd: PropTypes.func,
  resourceEstimates: PropTypes.array,
  setFieldValue: PropTypes.func,
  taskEstimatedCost: PropTypes.object,
  projectId: PropTypes.string,
  projectSlug: PropTypes.string,
  projectCurrency: PropTypes.object,
  dialogWidth: PropTypes.number,
  canViewCost: PropTypes.bool,
  values: PropTypes.object
};

export default TaskResourceEstimates;
